import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { usePMODashboard } from '../../../hooks/useDashboard';
import { Card } from '../../../components/common/Card';
import { Icons } from '../../../components/common/Icons';
import { Button } from '../../../components/common/Button';
import { DonutChart } from '../../../components/common/Charts';
import { BarChart } from '../../../components/common/Charts';
import { getGreeting, getFirstName } from '../../../utils/helpers';
import './PMODashboard.css';

/* ── Constants ── */

const PROJECT_TYPE_LABELS: Record<string, string> = {
  adm: 'ADM',
  tnm: 'T&M',
  staff_aug: 'Staff Aug',
  inhouse: 'In-house',
  digital_marketing: 'Digital Marketing',
  upwork: 'Upwork',
  outsource: 'Outsource',
};

const PIE_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899'];

const STATUS_COLORS: Record<string, string> = {
  approved: '#10b981',
  submitted: '#3b82f6',
  draft: '#94a3b8',
  rejected: '#ef4444',
};

/* ── Main Component ── */

export function PMODashboard() {
  const { user } = useAuth();
  const { data, isLoading, error, refetch } = usePMODashboard();
  const navigate = useNavigate();
  
  // Top projects filter state
  const [topProjectsFilter, setTopProjectsFilter] = useState<'all' | 'billable' | 'resources'>('all');

  if (error) {
    return (
      <div className="dashboard-error">
        <Icons.AlertCircle />
        <h2>Error loading dashboard</h2>
        <p>{error}</p>
        <button onClick={refetch}>Try Again</button>
      </div>
    );
  }

  if (!data || !user) {
    return (
      <div className="pmo-dashboard">
        <header className="dashboard-header-content">
          <div className="header-greeting">
            <h1>{getGreeting()}, {user?.name ? getFirstName(user.name) : 'PMO'}!</h1>
            <p>Loading dashboard...</p>
          </div>
        </header>
        {isLoading && <div className="pmo-loading"><div className="pmo-spinner" /></div>}
      </div>
    );
  }

  const billableTotal = data.billableHoursThisMonth || 0;
  const unbillableTotal = data.unbillableHoursThisMonth || 0;
  const billablePct = billableTotal + unbillableTotal > 0
    ? Math.round((billableTotal / (billableTotal + unbillableTotal)) * 100)
    : 0;

  const statCards = [
    { title: 'Total Clients',    value: String(data.totalClients || 0), icon: <Icons.Building />, color: '#8b5cf6', link: '/clients' },
    { title: 'Billable Projects',  value: String(data.billableProjectsCount ?? 0),    icon: <Icons.Folder />,   color: '#3b82f6', link: '/projects?billable=true' },
    { title: 'Billable Hours',   value: billableTotal.toLocaleString(undefined, { maximumFractionDigits: 0 }),  icon: <Icons.TrendingUp />,  color: '#10b981', sub: 'This month', link: '/all-timesheets' },
    { title: 'Non-Billable Hrs', value: unbillableTotal.toLocaleString(undefined, { maximumFractionDigits: 0 }), icon: <Icons.TrendingDown />, color: '#f59e0b', sub: 'This month', link: '/all-timesheets' },
    { title: 'Employees on Bench', value: String(data.benchEmployeeCount || 0), icon: <Icons.Users />, color: '#ef4444', sub: 'This month', link: '/bench-employees' },
  ];

  const getUtilizationColor = (pct: number) =>
    pct >= 90 ? '#ef4444' : pct >= 70 ? '#f59e0b' : '#10b981';

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' });
  };

  const projectTypeSlices = (data.projectTypeDistribution || []).map((d, i) => ({
    value: d.count,
    color: PIE_COLORS[i % PIE_COLORS.length],
    label: PROJECT_TYPE_LABELS[d.type] || d.type,
  }));

  const statusSlices = (data.timesheetStatusDistribution || []).map((d) => ({
    value: d.count,
    color: STATUS_COLORS[d.status] || '#94a3b8',
    label: d.status.charAt(0).toUpperCase() + d.status.slice(1),
  }));

  return (
    <div className="pmo-dashboard">
      {/* Header */}
      <header className="dashboard-header-content">
        <div className="header-greeting">
          <h1>{getGreeting()}, {getFirstName(user.name)}!</h1>
          <p>Here's your project management overview for this month.</p>
        </div>
        <div className="header-actions">
          <Button onClick={() => navigate('/clients')} variant="secondary"><Icons.Plus /> New Client</Button>
          <Button onClick={() => navigate('/projects')}><Icons.Plus /> New Project</Button>
        </div>
      </header>

      {/* Stat Cards */}
      <section className="stats-grid">
        {statCards.map((stat, index) => (
          <Card key={index} className="stat-card" padding="none" hoverable onClick={() => navigate(stat.link)}>
            <div className="stat-card-inner">
              <div className="stat-icon-wrapper" style={{ backgroundColor: `${stat.color}15`, color: stat.color }}>
                {stat.icon}
              </div>
              <div className="stat-details">
                <span className="stat-title">{stat.title}</span>
                <span className="stat-value">{stat.value}</span>
                {stat.sub && <span className="stat-period">{stat.sub}</span>}
              </div>
            </div>
          </Card>
        ))}
      </section>

      {/* Charts Row 1: Bar + Billable Donut */}
      <div className="pmo-charts-row">
        <Card title="Monthly Hours Trend" className="pmo-chart-card pmo-bar-chart-card">
          {data.hoursTrend && data.hoursTrend.length > 0 ? (
            <BarChart data={data.hoursTrend} />
          ) : (
            <div className="chart-empty"><Icons.Chart /><p>No trend data available</p></div>
          )}
        </Card>

        <Card title="Billable vs Non-Billable" className="pmo-chart-card">
          <DonutChart
            slices={[
              { value: billableTotal, color: '#10b981', label: 'Billable' },
              { value: unbillableTotal, color: '#f59e0b', label: 'Non-Billable' },
            ]}
            label={`${billablePct}%`}
            subLabel="Billable"
          />
        </Card>
      </div>

      {/* Charts Row 2: Project Types + Timesheet Status + Quick Stats */}
      <div className="pmo-charts-row pmo-three-col">
        <Card title="Project Types" className="pmo-chart-card">
          <DonutChart slices={projectTypeSlices} size={160} label={String(data.activeProjects)} subLabel="Active" />
        </Card>

        <Card title="Timesheet Status" className="pmo-chart-card">
          <DonutChart
            slices={statusSlices}
            size={160}
            label={String(statusSlices.reduce((s, sl) => s + sl.value, 0))}
            subLabel="This Month"
          />
        </Card>

        <Card title="Quick Stats" className="pmo-chart-card pmo-quick-stats-card">
          <div className="pmo-quick-stats">
            <div className="pmo-qs-item" onClick={() => navigate('/allocations')}>
              <div className="pmo-qs-icon" style={{ background: '#f59e0b20', color: '#f59e0b' }}><Icons.Clock /></div>
              <div className="pmo-qs-info">
                <span className="pmo-qs-value">{(data.pendingProjectRequests || 0) + (data.pendingLateEntries || 0)}</span>
                <span className="pmo-qs-label">Pending Requests</span>
              </div>
            </div>
            <div className="pmo-qs-item" onClick={() => navigate('/approvals')}>
              <div className="pmo-qs-icon" style={{ background: '#3b82f620', color: '#3b82f6' }}><Icons.Check /></div>
              <div className="pmo-qs-info">
                <span className="pmo-qs-value">{data.pendingTimesheets}</span>
                <span className="pmo-qs-label">Pending Timesheets</span>
              </div>
            </div>
            <div className="pmo-qs-item" onClick={() => navigate('/allocations?tab=unassigned')}>
              <div className="pmo-qs-icon" style={{ background: '#ef444420', color: '#ef4444' }}><Icons.Users /></div>
              <div className="pmo-qs-info">
                <span className="pmo-qs-value">{data.unassignedEmployees || 0}</span>
                <span className="pmo-qs-label">Unassigned Employees</span>
              </div>
            </div>
            <div className="pmo-qs-item">
              <div className="pmo-qs-icon" style={{ background: '#8b5cf620', color: '#8b5cf6' }}><Icons.Activity /></div>
              <div className="pmo-qs-info">
                <span className="pmo-qs-value">{Math.round(data.totalHoursThisMonth).toLocaleString()}</span>
                <span className="pmo-qs-label">Total Hours This Month</span>
              </div>
            </div>
          </div>
        </Card>
      </div>

      {/* Bottom Row: Projects + Pending Requests */}
      <div className="pmo-bottom-row">
        <Card className="pmo-projects-card">
          <div className="pmo-projects-header">
            <h3 className="card-title">Top 5 Stats</h3>
            <div className="pmo-filter-tabs">
              <button 
                className={`pmo-filter-tab ${topProjectsFilter === 'all' ? 'active' : ''}`}
                onClick={() => setTopProjectsFilter('all')}
              >
                All Projects
              </button>
              <button 
                className={`pmo-filter-tab ${topProjectsFilter === 'billable' ? 'active' : ''}`}
                onClick={() => setTopProjectsFilter('billable')}
              >
                Billable Projects
              </button>
              <button 
                className={`pmo-filter-tab ${topProjectsFilter === 'resources' ? 'active' : ''}`}
                onClick={() => setTopProjectsFilter('resources')}
              >
                Top Resources
              </button>
            </div>
          </div>
          
          {topProjectsFilter === 'all' && (
            (!data.projectsOverview || data.projectsOverview.length === 0) ? (
              <div className="chart-empty"><Icons.Folder /><p>No active projects</p></div>
            ) : (
              <>
                <div className="pmo-projects-list">
                  {data.projectsOverview.slice(0, 5).map((project) => (
                    <div key={project.projectId} className="pmo-project-row">
                      <div className="pmo-project-info">
                        <span className="pmo-project-name" title={project.projectName}>{project.projectName}</span>
                        {project.clientName && <span className="pmo-project-client">{project.clientName}</span>}
                      </div>
                      <div className="pmo-project-bar-container">
                        <div className="pmo-project-bar">
                          <div className="pmo-project-bar-fill"
                            style={{ width: `${Math.min(project.utilizationPercentage, 100)}%`, backgroundColor: getUtilizationColor(project.utilizationPercentage) }} />
                        </div>
                        <span className="pmo-project-pct" style={{ color: getUtilizationColor(project.utilizationPercentage) }}>
                          {Math.round(project.utilizationPercentage)}%
                        </span>
                      </div>
                      <span className="pmo-project-hours">{Math.round(project.totalHours).toLocaleString()} hrs</span>
                    </div>
                  ))}
                </div>
                <button className="view-all-btn" onClick={() => navigate('/projects')}>View All Projects →</button>
              </>
            )
          )}
          
          {topProjectsFilter === 'billable' && (
            (!data.topBillableProjects || data.topBillableProjects.length === 0) ? (
              <div className="chart-empty"><Icons.TrendingUp /><p>No billable hours this month</p></div>
            ) : (
              <>
                <div className="pmo-projects-list">
                  {data.topBillableProjects.slice(0, 5).map((project, idx) => {
                    const maxHours = data.topBillableProjects[0]?.totalHours || 1;
                    const pct = (project.totalHours / maxHours) * 100;
                    return (
                      <div key={project.projectId} className="pmo-project-row">
                        <div className="pmo-project-info">
                          <span className="pmo-project-name" title={project.projectName}>{project.projectName}</span>
                          {project.clientName && <span className="pmo-project-client">{project.clientName}</span>}
                        </div>
                        <div className="pmo-project-bar-container">
                          <div className="pmo-project-bar">
                            <div className="pmo-project-bar-fill"
                              style={{ width: `${pct}%`, backgroundColor: '#10b981' }} />
                          </div>
                        </div>
                        <span className="pmo-project-hours">{Math.round(project.totalHours).toLocaleString()} hrs</span>
                      </div>
                    );
                  })}
                </div>
                <button className="view-all-btn" onClick={() => navigate('/projects?billable=true')}>View Billable Projects →</button>
              </>
            )
          )}
          
          {topProjectsFilter === 'resources' && (
            (!data.topResourcesByBillableHours || data.topResourcesByBillableHours.length === 0) ? (
              <div className="chart-empty"><Icons.Users /><p>No billable hours logged this month</p></div>
            ) : (
              <>
                <div className="pmo-projects-list">
                  {data.topResourcesByBillableHours.slice(0, 5).map((resource, idx) => {
                    const maxHours = data.topResourcesByBillableHours[0]?.totalBillableHours || 1;
                    const pct = (resource.totalBillableHours / maxHours) * 100;
                    return (
                      <div key={resource.employeeId} className="pmo-project-row">
                        <div className="pmo-project-info">
                          <span className="pmo-project-name" title={resource.employeeName}>{resource.employeeName}</span>
                          {resource.department && <span className="pmo-project-client">{resource.department}</span>}
                        </div>
                        <div className="pmo-project-bar-container">
                          <div className="pmo-project-bar">
                            <div className="pmo-project-bar-fill"
                              style={{ width: `${pct}%`, backgroundColor: '#8b5cf6' }} />
                          </div>
                        </div>
                        <span className="pmo-project-hours">{Math.round(resource.totalBillableHours).toLocaleString()} hrs</span>
                      </div>
                    );
                  })}
                </div>
                <button className="view-all-btn" onClick={() => navigate('/allocations')}>View All Resources →</button>
              </>
            )
          )}
        </Card>

        <Card title="Pending Requests" className="pmo-requests-card">
          <div className="requests-content">
            {(data.recentProjectRequests?.length || 0) === 0 && (data.recentLateEntries?.length || 0) === 0 ? (
              <div className="chart-empty"><Icons.CheckCircle /><p>All clear! No pending requests.</p></div>
            ) : (
              <>
                {data.recentProjectRequests?.slice(0, 3).map((req) => (
                  <div key={`pr-${req.id}`} className="request-item">
                    <div className="request-icon project"><Icons.Folder /></div>
                    <div className="request-info">
                      <span className="request-name">{req.employeeName}</span>
                      <span className="request-detail">→ {req.projectName}</span>
                    </div>
                    <span className="request-date">{formatDate(req.createdAt)}</span>
                  </div>
                ))}
                {data.recentLateEntries?.slice(0, 2).map((req) => (
                  <div key={`le-${req.id}`} className="request-item">
                    <div className="request-icon late"><Icons.Clock /></div>
                    <div className="request-info">
                      <span className="request-name">{req.employeeName}</span>
                      <span className="request-detail">Late entry: {formatDate(req.requestedDate)}</span>
                    </div>
                    <span className="request-date">{formatDate(req.createdAt)}</span>
                  </div>
                ))}
              </>
            )}
          </div>
          <button className="view-all-btn" onClick={() => navigate('/allocations')}>View All Requests →</button>
        </Card>
      </div>
    </div>
  );
}

export default PMODashboard;
